import React, { useState, useRef, useEffect, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { toast } from "sonner";
import ReactMarkdown from "react-markdown";
import jsPDF from "jspdf";
import DashboardLayout from "@/components/DashboardLayout";
import { API } from "@/lib/api";
import { Send, Plus, Trash2, ShieldCheck, Copy, Download, Pin, PinOff, Star } from "lucide-react";

const DEFAULT_SUGGESTIONS = [
  "What should I reorder this week?",
  "Which SKUs could stock out within two weeks?",
  "Where do I have the most money tied up?",
  "Show my highest-margin fast movers.",
];

const RECORD_SEP = "\u241E";

function fmtRelative(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 86400 * 7) return `${Math.floor(diff / 86400)}d ago`;
  return d.toLocaleDateString();
}

export default function AskPage() {
  const [demo, setDemo] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [sessions, setSessions] = useState([]);
  const [sessionId, setSessionId] = useState(null);
  const [grounding, setGrounding] = useState(null);
  const [pinned, setPinned] = useState([]);
  const [model, setModel] = useState(() => localStorage.getItem("tm_ask_model") || "sonnet");
  const endRef = useRef(null);
  const loc = useLocation();

  useEffect(() => { localStorage.setItem("tm_ask_model", model); }, [model]);

  const MODELS = [
    { key: "haiku",  label: "Haiku",  hint: "Fastest · quick lookups" },
    { key: "sonnet", label: "Sonnet", hint: "Balanced · default" },
    { key: "opus",   label: "Opus",   hint: "Deepest · big analyses" },
  ];

  const token = () => localStorage.getItem("tm_token");
  const authHeaders = () => ({ Authorization: `Bearer ${token()}` });
  const newMsg = (role, content) => ({
    id: (crypto?.randomUUID?.() || `m-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`),
    role,
    content,
  });

  const loadSessions = useCallback(async () => {
    try {
      const res = await fetch(`${API}/ask/sessions`, { headers: authHeaders() });
      if (res.ok) setSessions((await res.json()).sessions || []);
    } catch (err) { console.debug("loadSessions failed", err); }
  }, []);

  const loadPinned = useCallback(async () => {
    try {
      const res = await fetch(`${API}/pinned-prompts`, { headers: authHeaders() });
      if (res.ok) setPinned((await res.json()).pinned || []);
    } catch (err) { console.debug("loadPinned failed", err); }
  }, []);

  const openSession = useCallback(async (sid) => {
    setSessionId(sid);
    setMessages([]);
    setGrounding(null);
    try {
      const res = await fetch(`${API}/ask/sessions/${sid}`, { headers: authHeaders() });
      if (!res.ok) return;
      const data = await res.json();
      setMessages((data.messages || []).map((m) => ({
        id: (crypto?.randomUUID?.() || `srv-${Math.random().toString(36).slice(2, 10)}`),
        role: m.role,
        content: m.content,
      })));
      const last = [...(data.messages || [])].reverse().find((m) => m.role === "assistant" && m.grounding);
      if (last) setGrounding(last.grounding);
    } catch (err) { console.debug("openSession failed", err); }
  }, []);

  const newChat = useCallback(() => {
    setSessionId(null); setMessages([]); setGrounding(null); setInput("");
  }, []);

  const deleteSession = async (sid, e) => {
    e.stopPropagation();
    if (!window.confirm("Delete this conversation?")) return;
    await fetch(`${API}/ask/sessions/${sid}`, { method: "DELETE", headers: authHeaders() });
    if (sid === sessionId) newChat();
    loadSessions();
  };

  const pinPrompt = async (text) => {
    const t = (text || "").trim();
    if (!t) return;
    try {
      const res = await fetch(`${API}/pinned-prompts`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify({ text: t }),
      });
      if (res.ok) { toast.success("Pinned"); loadPinned(); }
      else { const j = await res.json().catch(() => ({})); toast.error(j.detail || "Pin failed"); }
    } catch (err) { console.error("pinPrompt failed", err); toast.error("Pin failed"); }
  };

  const unpinPrompt = async (id) => {
    await fetch(`${API}/pinned-prompts/${id}`, { method: "DELETE", headers: authHeaders() });
    loadPinned();
  };

  const isPinned = (text) => pinned.some((p) => p.text === (text || "").trim());

  useEffect(() => { loadSessions(); loadPinned(); }, [loadSessions, loadPinned]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);
  useEffect(() => {
    const pre = loc.state?.prefill;
    if (pre && !messages.length) { setInput(pre); }
  }, [loc.state, messages.length]);

  const send = async (text) => {
    const q = (text ?? input).trim();
    if (!q || busy) return;
    setInput("");
    const userMsg = newMsg("user", q);
    const assistantMsg = newMsg("assistant", "");
    setMessages((m) => [...m, userMsg, assistantMsg]);
    setBusy(true);
    try {
      const res = await fetch(`${API}/ask?demo=${demo}`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify({ question: q, session_id: sessionId, model }),
      });
      const ct = res.headers.get("content-type") || "";
      if (!res.ok || ct.includes("application/json")) {
        const data = await res.json().catch(() => ({}));
        setMessages((m) => { const c = [...m]; c[c.length - 1].content = data.answer || data.detail || "AI service error."; return c; });
        return;
      }
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let acc = "";
      let headerParsed = false;
      let buffer = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        if (!headerParsed) {
          if (buffer.indexOf(RECORD_SEP) === 0) {
            const second = buffer.indexOf(RECORD_SEP, 1);
            if (second === -1) continue;
            try {
              const meta = JSON.parse(buffer.slice(1, second));
              if (meta.session_id) setSessionId(meta.session_id);
              if (meta.grounding) setGrounding(meta.grounding);
            } catch (err) { console.debug("stream header parse failed", err); }
            buffer = buffer.slice(second + 1);
          }
          headerParsed = true;
        }
        if (buffer) {
          acc += buffer; buffer = "";
          setMessages((m) => { const c = [...m]; c[c.length - 1].content = acc; return c; });
        }
      }
      loadSessions();
    } catch (err) {
      console.error("Ask stream failed", err);
      setMessages((m) => { const c = [...m]; c[c.length - 1].content = "Network error."; return c; });
    } finally { setBusy(false); }
  };

  // ---------- Export helpers ----------
  const transcriptText = () => messages.map((m) => `${m.role === "user" ? "You" : "TreadMargin"}: ${m.content}`).join("\n\n");

  const copyTranscript = async () => {
    if (!messages.length) { toast.error("Nothing to copy yet."); return; }
    try {
      await navigator.clipboard.writeText(transcriptText());
      toast.success("Transcript copied.");
    } catch (err) { console.error("clipboard write failed", err); toast.error("Copy failed — long-press the chat to copy manually."); }
  };

  const downloadPdf = () => {
    if (!messages.length) { toast.error("Nothing to export yet."); return; }
    const doc = new jsPDF({ unit: "pt", format: "letter" });
    const margin = 54;
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const maxWidth = pageWidth - margin * 2;
    let y = margin;

    doc.setFont("helvetica", "bold");
    doc.setFontSize(16);
    doc.setTextColor(15, 17, 21);
    doc.text("TreadMargin — Ask transcript", margin, y);
    y += 22;

    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.setTextColor(90, 96, 106);
    const stamp = new Date().toLocaleString();
    doc.text(`Exported ${stamp}${grounding ? `  ·  Grounded in ${grounding.sku_count} SKUs / ${grounding.sales_rows} sales rows / ${grounding.observed_days}-day window` : ""}`, margin, y);
    y += 20;
    doc.setDrawColor(220, 216, 208);
    doc.line(margin, y, pageWidth - margin, y);
    y += 18;

    messages.forEach((m) => {
      doc.setFont("helvetica", "bold");
      doc.setFontSize(11);
      doc.setTextColor(m.role === "user" ? 15 : 20, m.role === "user" ? 17 : 98, m.role === "user" ? 21 : 74);
      const label = m.role === "user" ? "You" : "TreadMargin";
      if (y > pageHeight - margin - 40) { doc.addPage(); y = margin; }
      doc.text(label, margin, y);
      y += 14;

      doc.setFont("helvetica", "normal");
      doc.setFontSize(11);
      doc.setTextColor(30, 34, 41);
      const lines = doc.splitTextToSize(m.content || "", maxWidth);
      lines.forEach((line) => {
        if (y > pageHeight - margin) { doc.addPage(); y = margin; }
        doc.text(line, margin, y);
        y += 14;
      });
      y += 8;
    });

    // Footer on last page
    doc.setFontSize(8);
    doc.setTextColor(140, 145, 152);
    doc.text("TreadMargin · Grounded in your uploaded data.", margin, pageHeight - 30);

    const first = (messages.find((m) => m.role === "user")?.content || "conversation").slice(0, 40).replace(/[^a-z0-9]+/gi, "-");
    doc.save(`treadmargin-${first || "conversation"}.pdf`);
    toast.success("PDF downloaded.");
  };

  const suggestions = pinned.length ? pinned.map((p) => p.text) : DEFAULT_SUGGESTIONS;

  return (
    <DashboardLayout demoBanner={demo} demoMode={demo} onToggleDemo={() => setDemo(!demo)}>
      <div className="flex flex-col lg:flex-row gap-6 max-w-6xl">
        {/* Sidebar */}
        <aside className="lg:w-64 shrink-0" data-testid="ask-sidebar">
          <button
            onClick={newChat}
            className="tm-btn-outline w-full h-10 flex items-center justify-center gap-1.5 text-sm"
            data-testid="ask-new-chat"
          ><Plus size={14} /> New conversation</button>

          {pinned.length > 0 && (
            <>
              <div className="mt-4 text-[11px] uppercase tracking-wider text-[#3B424B] font-semibold px-1 flex items-center gap-1"><Pin size={11} /> Pinned prompts</div>
              <div className="mt-2 space-y-1" data-testid="ask-pinned-list">
                {pinned.map((p) => (
                  <div key={p.id} className="group flex items-center gap-1 px-2 py-1.5 rounded-md border border-transparent hover:border-[var(--tm-border)] hover:bg-[var(--tm-off)]" data-testid={`ask-pinned-${p.id}`}>
                    <button
                      onClick={() => { newChat(); setInput(p.text); setTimeout(() => send(p.text), 30); }}
                      className="flex-1 min-w-0 text-left text-xs text-[#0F1115] truncate"
                      title={p.text}
                    >{p.text}</button>
                    <button
                      onClick={() => unpinPrompt(p.id)}
                      className="opacity-0 group-hover:opacity-100 p-1 text-[#6b6f77] hover:text-red-600"
                      title="Unpin"
                      data-testid={`ask-unpin-${p.id}`}
                    ><PinOff size={12} /></button>
                  </div>
                ))}
              </div>
            </>
          )}

          <div className="mt-4 text-[11px] uppercase tracking-wider text-[#3B424B] font-semibold px-1">Recent</div>
          <div className="mt-2 space-y-1 max-h-[45vh] overflow-y-auto tm-scroll pr-1">
            {sessions.length === 0 && <div className="text-xs text-[#6b6f77] px-1 py-3">No conversations yet.</div>}
            {sessions.map((s) => (
              <button
                key={s.id}
                onClick={() => openSession(s.id)}
                className={`group w-full text-left px-2.5 py-2 rounded-md border transition-colors ${sessionId === s.id ? "border-[var(--tm-charcoal)] bg-[var(--tm-off)]" : "border-transparent hover:border-[var(--tm-border)] hover:bg-[var(--tm-off)]"}`}
                data-testid={`ask-session-${s.id}`}
              >
                <div className="flex items-start gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-[#0F1115] truncate leading-tight">{s.title}</div>
                    <div className="text-[11px] text-[#6b6f77] mt-0.5">{fmtRelative(s.updated_at)}</div>
                  </div>
                  <span
                    onClick={(e) => deleteSession(s.id, e)}
                    className="opacity-0 group-hover:opacity-100 p-1 text-[#6b6f77] hover:text-red-600 cursor-pointer"
                    data-testid={`ask-delete-${s.id}`}
                    title="Delete"
                  ><Trash2 size={13} /></span>
                </div>
              </button>
            ))}
          </div>
        </aside>

        {/* Chat pane */}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold" data-testid="ask-heading">Ask TreadMargin</h1>
              <p className="mt-1 text-sm text-[#3B424B]">Grounded in your uploaded data. If something can't be answered from your data, we'll say so.</p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {grounding && (
                <div className="tm-card bg-white px-3 py-2 text-xs flex items-center gap-2 text-[#0F1115]" data-testid="ask-grounding-badge">
                  <ShieldCheck size={14} className="text-[var(--tm-forest)]" />
                  <span>
                    Grounded in <b>{grounding.sku_count}</b> SKUs · <b>{grounding.sales_rows}</b> sales rows · <b>{grounding.observed_days}</b>-day window
                    {grounding.demo && <span className="ml-1 text-[#8a6318]">· Illustrative Demo</span>}
                  </span>
                </div>
              )}
              <button
                onClick={copyTranscript}
                className="tm-btn-outline h-9 px-3 inline-flex items-center gap-1.5 text-xs"
                disabled={!messages.length}
                data-testid="ask-copy"
              ><Copy size={13} /> Copy</button>
              <button
                onClick={downloadPdf}
                className="tm-btn-outline h-9 px-3 inline-flex items-center gap-1.5 text-xs"
                disabled={!messages.length}
                data-testid="ask-download-pdf"
              ><Download size={13} /> Export PDF</button>
            </div>
          </div>

          <div className="mt-6 tm-card bg-white overflow-hidden flex flex-col h-[60vh]">
            <div className="flex-1 overflow-y-auto tm-scroll p-5 space-y-4">
              {messages.length === 0 ? (
                <div>
                  <div className="text-xs uppercase tracking-wider text-[#3B424B] font-semibold mb-3">
                    {pinned.length ? "Your pinned prompts" : "Try asking"}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {suggestions.map((s) => (
                      <button
                        key={s}
                        onClick={() => send(s)}
                        className="tm-btn-outline h-9 px-3 text-xs"
                        data-testid={`ask-suggestion-${s.slice(0, 10)}`}
                      >{s}</button>
                    ))}
                  </div>
                  {!pinned.length && (
                    <p className="mt-4 text-[11px] text-[#6b6f77]">Tip: hover a message and click the pin icon to save prompts you use every week.</p>
                  )}
                </div>
              ) : messages.map((m, i) => (
                <div key={m.id} className={`group flex items-end gap-1.5 ${m.role === "user" ? "justify-end" : "justify-start"}`} data-testid={`ask-msg-${i}`}>
                  {m.role === "user" && (
                    <button
                      onClick={() => (isPinned(m.content) ? unpinPrompt(pinned.find((p) => p.text === m.content.trim()).id) : pinPrompt(m.content))}
                      className="opacity-0 group-hover:opacity-100 p-1 text-[#6b6f77] hover:text-[var(--tm-forest)]"
                      title={isPinned(m.content) ? "Unpin" : "Pin this prompt"}
                      data-testid={`ask-pin-btn-${i}`}
                    ><Star size={13} fill={isPinned(m.content) ? "currentColor" : "none"} /></button>
                  )}
                  <div className={`max-w-[80%] rounded-lg px-4 py-2.5 text-sm leading-relaxed ${m.role === "user" ? "bg-[var(--tm-charcoal)] text-white whitespace-pre-wrap" : "bg-[var(--tm-off)] text-[#0F1115] ask-md"}`}>
                    {m.content ? (
                      m.role === "assistant" ? (
                        <ReactMarkdown
                          components={{
                            p: ({ node, ...props }) => <p className="mb-2 last:mb-0" {...props} />,
                            ul: ({ node, ...props }) => <ul className="list-disc pl-5 my-2 space-y-0.5" {...props} />,
                            ol: ({ node, ...props }) => <ol className="list-decimal pl-5 my-2 space-y-0.5" {...props} />,
                            li: ({ node, ...props }) => <li className="leading-snug" {...props} />,
                            strong: ({ node, ...props }) => <strong className="font-semibold text-[#0F1115]" {...props} />,
                            code: ({ node, ...props }) => <code className="px-1 rounded bg-white/60 font-mono text-[12px]" {...props} />,
                            a: ({ node, ...props }) => <a className="underline text-[var(--tm-forest)]" target="_blank" rel="noreferrer" {...props} />,
                          }}
                        >
                          {m.content}
                        </ReactMarkdown>
                      ) : m.content
                    ) : <span className="opacity-50">Thinking…</span>}
                  </div>
                </div>
              ))}
              <div ref={endRef} />
            </div>
            <form onSubmit={(e) => { e.preventDefault(); send(); }} className="border-t border-[var(--tm-border)] p-3 flex flex-col gap-2">
              <div className="flex items-center gap-1.5 text-[11px] text-[#3B424B]" data-testid="ask-model-picker">
                <span className="uppercase tracking-wider font-semibold mr-1">Claude</span>
                {MODELS.map((m) => (
                  <button
                    key={m.key}
                    type="button"
                    onClick={() => setModel(m.key)}
                    title={m.hint}
                    className={`px-2.5 h-7 rounded-md border text-[11px] font-medium transition-colors ${model === m.key ? "border-[var(--tm-charcoal)] bg-[var(--tm-charcoal)] text-white" : "border-[var(--tm-border)] text-[#0F1115] hover:bg-[var(--tm-off)]"}`}
                    data-testid={`ask-model-${m.key}`}
                    aria-pressed={model === m.key}
                  >{m.label}</button>
                ))}
                <span className="ml-2 text-[10px] text-[#8b8f97] hidden sm:inline">{MODELS.find((x) => x.key === model)?.hint}</span>
              </div>
              <div className="flex gap-2 items-center">
                <input
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder="Ask about your inventory or sales…"
                  className="tm-input flex-1"
                  data-testid="ask-input"
                />
                {input.trim() && !isPinned(input) && (
                  <button type="button" onClick={() => pinPrompt(input)} className="tm-btn-outline h-11 px-3" title="Pin this prompt" data-testid="ask-pin-input">
                    <Pin size={14} />
                  </button>
                )}
                <button type="submit" disabled={busy || !input.trim()} className="tm-btn-primary h-11 px-4" data-testid="ask-send">
                  <Send size={15} />
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
